import { useMemo, useState } from 'react';
import { formatCurrency } from '../../../utils/formatCurrency';

/**
 * RevenueChart - 2026 SaaS Standard with Adaptive Low-Data Handling
 * Stripe / Linear / Ramp level
 * Gracefully handles sparse datasets with intelligent scaling
 */
export default function RevenueChart({ data = [], height = 200 }) {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    
    const dataMax = Math.max(...data.map(d => d.value));
    const dataMin = Math.min(...data.map(d => d.value));
    const dataRange = dataMax - dataMin;
    
    // Aggressive Y-axis scaling for calm visualization
    // Prevents dramatic stretching with sparse/low-variance data
    const varianceRatio = dataRange === 0 ? 0 : dataRange / dataMax;
    const paddingRatio = dataRange === 0 
      ? 0.35 
      : Math.min(0.4, 0.2 + (0.2 * (1 / Math.max(0.1, varianceRatio))));
    const padding = Math.max(dataRange * paddingRatio, dataMax * 0.12);
    
    const scaledMax = dataMax + padding;
    const scaledMin = Math.max(0, dataMin - padding * 0.4);
    const scaledRange = scaledMax - scaledMin || 1;
    
    const width = 100;
    const verticalPadding = 6; // Minimal padding for grounded feel
    const chartHeight = height - verticalPadding * 2;
    
    // Detect low-data scenario
    const isLowData = data.length <= 3 || varianceRatio < 0.15;
    
    // Very calm curve tension
    const curveTension = isLowData ? 0.04 : 0.08;
    
    const points = data.map((item, index) => {
      const x = (index / Math.max(1, data.length - 1)) * width;
      const y = verticalPadding + chartHeight - ((item.value - scaledMin) / scaledRange) * chartHeight;
      return { x, y, value: item.value, label: item.label };
    });
    
    // Very restrained curve with minimal tension
    const createPath = (points) => {
      if (points.length < 2) return '';
      
      let path = `M ${points[0].x} ${points[0].y}`;
      
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];
        
        const cp1x = p1.x + (p2.x - p0.x) * curveTension;
        const cp1y = p1.y + (p2.y - p0.y) * curveTension;
        const cp2x = p2.x - (p3.x - p1.x) * curveTension;
        const cp2y = p2.y - (p3.y - p1.y) * curveTension;
        
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }
      
      return path;
    };
    
    const smoothPath = createPath(points);
    
    return { 
      points, 
      smoothPath, 
      isLowData,
      dataPoints: data.length,
      varianceRatio
    };
  }, [data, height]);

  if (!chartData || !data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center text-slate-400 text-xs"
        style={{ height: `${height}px` }}
      >
        No revenue data yet
      </div>
    );
  }

  const { points, smoothPath, isLowData, dataPoints, varianceRatio } = chartData;
  const areaPath = `${smoothPath} L ${points[points.length - 1].x} ${height} L 0 ${height} Z`;

  return (
    <div className="relative group/chart" style={{ height: `${height}px` }}>
      {/* Contextual indicator for low data - extremely subtle */}
      {isLowData && (
        <div className="absolute top-0 right-0 text-[9px] text-slate-400">
          Early data
        </div>
      )}
      
      {/* Refined tooltip - Stripe style */}
      {hoveredPoint !== null && (
        <div 
          className="absolute z-50 pointer-events-none transition-all duration-150 ease-out"
          style={{
            left: `${hoveredPoint.x}%`,
            top: `${hoveredPoint.y - 42}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="bg-slate-900/95 rounded-md px-2.5 py-1.5 
            shadow-[0_2px_12px_rgba(0,0,0,0.25)]">
            <div className="text-[9px] text-slate-400 mb-0.5">
              {hoveredPoint.label}
            </div>
            <div className="text-sm font-semibold text-white tabular-nums">
              {formatCurrency(hoveredPoint.value)}
            </div>
          </div>
        </div>
      )}
      
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full h-full opacity-0 animate-[fadeIn_180ms_ease-out_forwards]"
      >
        <defs>
          {/* Extremely subtle gradient */}
          <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" stopOpacity={varianceRatio < 0.1 ? "0.015" : "0.025"} />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.001" />
          </linearGradient>
        </defs>
        
        {/* Subtle structural grid */}
        <g opacity={isLowData ? "0.1" : "0.15"}>
          {[0.33, 0.67].map((ratio, i) => (
            <line
              key={i}
              x1="0"
              y1={height * ratio}
              x2="100"
              y2={height * ratio}
              stroke="rgba(148,163,184,0.25)"
              strokeWidth="0.3"
            />
          ))}
        </g>
        
        {/* Baseline anchor */}
        <line
          x1="0"
          y1={height - 6}
          x2="100"
          y2={height - 6}
          stroke="rgba(148,163,184,0.15)"
          strokeWidth="0.4"
        />
        
        {/* Subtle area fill */}
        <path
          d={areaPath}
          fill="url(#revenueGradient)"
        />
        
        {/* Thin, elegant main line */}
        <path
          d={smoothPath}
          fill="none"
          stroke="#6366f1"
          strokeWidth="1.1"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.75"
          className="transition-opacity duration-150"
        />
        
        {/* Hidden points by default - only show on hover and last point */}
        {points.map((point, index) => {
          const isHovered = hoveredPoint?.index === index;
          const isLast = index === points.length - 1;
          const showPoint = isHovered || isLast;
          
          if (!showPoint) return null;
          
          return (
            <g key={index}>
              {/* Subtle hover ring */}
              {isHovered && (
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="2.5"
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="0.4"
                  opacity="0.12"
                />
              )}
              
              {/* Clean data point */}
              <circle
                cx={point.x}
                cy={point.y}
                r={isHovered ? "1.6" : "1.2"}
                fill="white"
                stroke="#6366f1"
                strokeWidth="1.2"
                className="transition-all duration-150"
              />
            </g>
          );
        })}
        
        {/* Invisible hover targets for all points */}
        {points.map((point, index) => (
          <circle
            key={`target-${index}`}
            cx={point.x}
            cy={point.y}
            r="8"
            fill="transparent"
            className="cursor-pointer"
            onMouseEnter={() => setHoveredPoint({ ...point, index, x: (point.x / 100) * 100 })}
            onMouseLeave={() => setHoveredPoint(null)}
          />
        ))}
        
        {/* Subtle crosshair */}
        {hoveredPoint !== null && (
          <line
            x1={hoveredPoint.x}
            y1="0"
            x2={hoveredPoint.x}
            y2={height}
            stroke="rgba(99,102,241,0.06)"
            strokeWidth="0.5"
          />
        )}
      </svg>
      
      {/* Clean axis labels - adaptive spacing */}
      <div className="flex justify-between mt-1.5 px-0.5">
        {points.map((point, index) => {
          const isHovered = hoveredPoint?.index === index;
          // For many points, show fewer labels
          const shouldShowLabel = dataPoints <= 4 || index % Math.ceil(dataPoints / 6) === 0 || index === points.length - 1;
          
          return (
            <div 
              key={index} 
              className={`text-[9px] text-center font-medium transition-colors duration-150 ${
                isHovered 
                  ? 'text-slate-600' 
                  : 'text-slate-400'
              } ${!shouldShowLabel ? 'opacity-0' : ''}`}
              style={{ width: `${100 / points.length}%` }}
            >
              {point.label}
            </div>
          );
        })}
      </div>
      
      {/* CSS for fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
